/**
 * User Dormant Analytics Component
 * Displays comprehensive dormant user analysis including AI insights
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  LinearProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AppsIcon from '@mui/icons-material/Apps';
import { alpha, useTheme } from '@mui/material/styles';
import profile360Service from '../services/profile360Service';
import { useUserMetadata } from '../../../context/UserMetadataContext';

/**
 * Main User Dormant Analytics Component
 * @param {Object} props
 * @param {number} props.userId - User ID to analyze
 * @param {number} props.defaultThreshold - Default days threshold (optional, default: 90)
 */
export default function UserDormantAnalytics({ userId, defaultThreshold = 90 }) {
  const theme = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const { userMetadata } = useUserMetadata();
  const tenantName = userMetadata?.company || 'trustx';
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [daysThreshold, setDaysThreshold] = useState(defaultThreshold);
  const [expanded, setExpanded] = useState('user-info'); // Default expanded panel

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Initialize service with auth token and tenant
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
          scope: "openid profile email"
        }
      });
      profile360Service.initialize(token, tenantName);
      
      const response = await profile360Service.getUserDormantAnalytics(userId, daysThreshold);

      if (response.success) {
        setData(response.data);
      } else {
        setError(response.error || 'Failed to fetch dormant analytics');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [userId, daysThreshold, getAccessTokenSilently, tenantName]);

  useEffect(() => {
    if (userId) {
      fetchAnalytics();
    }
  }, [userId, fetchAnalytics]);

  const handleRefresh = () => {
    fetchAnalytics();
  };

  const handleThresholdChange = (event) => {
    setDaysThreshold(event.target.value);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Helper functions
  const getDormantStatusText = (isDormant) => {
    return isDormant ? 'Dormant' : 'Active';
  };

  const getDormantStatusIcon = (isDormant) => {
    return isDormant ? <PersonOffIcon /> : <CheckCircleIcon />;
  };

  const getRiskScoreColor = (score) => {
    if (score >= 8) return theme.palette.error.main;
    if (score >= 5) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const getPriorityColor = (priority) => {
    const p = String(priority || '').toUpperCase();
    if (p === 'HIGH') return 'error';
    if (p === 'MEDIUM') return 'warning';
    return 'info';
  };

  // Loading state
  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button size="small" onClick={handleRefresh} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  // No data state
  if (!data) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No analytics data available for this user.
      </Alert>
    );
  }

  const { user, dormant_status, ai_insight, stale_apps, metadata } = data;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Controls */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Dormant User Analytics
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Days Threshold</InputLabel>
            <Select
              value={daysThreshold}
              onChange={handleThresholdChange}
              label="Days Threshold"
            >
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={14}>14 days</MenuItem>
              <MenuItem value={30}>30 days</MenuItem>
              <MenuItem value={60}>60 days</MenuItem>
              <MenuItem value={90}>90 days</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Loading overlay */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* User Info Card */}
      <Accordion 
        expanded={expanded === 'user-info'} 
        onChange={handleAccordionChange('user-info')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            User Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body1">{user?.email || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Display Name</Typography>
              <Typography variant="body1">{user?.display_name || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Job Title</Typography>
              <Typography variant="body1">{user?.job_title || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Department</Typography>
              <Typography variant="body1">{user?.department || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Account Status</Typography>
              <Chip 
                label={user?.account_status || 'Unknown'} 
                size="small" 
                color={user?.account_status === 'active' ? 'success' : 'default'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Created At</Typography>
              <Typography variant="body1">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Dormant Status Card */}
      <Accordion 
        expanded={expanded === 'dormant-status'} 
        onChange={handleAccordionChange('dormant-status')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon color="primary" />
              Dormant Status
            </Typography>
            <Chip
              icon={getDormantStatusIcon(dormant_status?.is_dormant)}
              label={getDormantStatusText(dormant_status?.is_dormant)}
              color={dormant_status?.is_dormant ? 'error' : 'success'}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Days Inactive
                </Typography>
                <Typography variant="h4" color="text.primary">
                  {dormant_status?.days_inactive ?? '-'}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.1), textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Activities
                </Typography>
                <Typography variant="h4" color="text.primary">
                  {dormant_status?.activity_count_total ?? 0}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Recent Activities
                </Typography>
                <Typography variant="h4" color="text.primary">
                  {dormant_status?.activity_count_recent ?? 0}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.1), textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Unique Apps
                </Typography>
                <Typography variant="h4" color="text.primary">
                  {dormant_status?.unique_apps_accessed_recent ?? 0}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Last Activity</Typography>
              <Typography variant="body1">
                {dormant_status?.last_activity 
                  ? new Date(dormant_status.last_activity).toLocaleString() 
                  : 'No recent activity'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Stale Apps Count</Typography>
              <Typography variant="body1">{dormant_status?.stale_apps_count ?? 0}</Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* AI Insights Card */}
      {ai_insight && (
        <Accordion 
          expanded={expanded === 'ai-insights'} 
          onChange={handleAccordionChange('ai-insights')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SmartToyIcon color="primary" />
                AI-Generated Insights
              </Typography>
              <Chip
                label={ai_insight.classification || 'UNKNOWN'}
                color={ai_insight.classification === 'DORMANT' ? 'error' : 'warning'}
                size="small"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {/* Risk Score */}
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Risk Score
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress
                      variant="determinate"
                      value={(ai_insight.risk_score / 10) * 100}
                      size={80}
                      thickness={5}
                      sx={{ color: getRiskScoreColor(ai_insight.risk_score) }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="h5" component="div" color="text.primary">
                        {ai_insight.risk_score}/10
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>

              {/* Confidence Score */}
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Confidence Score
                  </Typography>
                  <Typography variant="h4" color="text.primary" sx={{ mt: 2 }}>
                    {((ai_insight.confidence_score || 0) * 100).toFixed(0)}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(ai_insight.confidence_score || 0) * 100} 
                    sx={{ mt: 2 }}
                  />
                </Card>
              </Grid>

              {/* Days Inactive */}
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Days Inactive (AI)
                  </Typography>
                  <Typography variant="h4" color="text.primary" sx={{ mt: 2 }}>
                    {ai_insight.days_inactive ?? '-'}
                  </Typography>
                </Card>
              </Grid>

              {/* Reasoning */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="primary" fontSize="small" />
                  AI Analysis
                </Typography>
                <Card sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <Typography variant="body2" color="text.secondary">
                    {ai_insight.reasoning || 'No reasoning provided'}
                  </Typography>
                </Card>
              </Grid>

              {/* Risk Factors */}
              {ai_insight.risk_factors && ai_insight.risk_factors.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon color="error" fontSize="small" />
                    Risk Factors
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {ai_insight.risk_factors.map((factor, index) => (
                      <Chip
                        key={index}
                        label={factor}
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Mitigating Factors */}
              {ai_insight.mitigating_factors && ai_insight.mitigating_factors.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    Mitigating Factors
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {ai_insight.mitigating_factors.map((factor, index) => (
                      <Chip
                        key={index}
                        label={factor}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Recommendations */}
              {ai_insight.recommendations && ai_insight.recommendations.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Recommended Actions
                  </Typography>
                  {ai_insight.recommendations.map((rec, index) => (
                    <Card key={index} sx={{ p: 2, mb: 1, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" fontWeight="bold">
                          {rec.action}
                        </Typography>
                        <Chip
                          label={rec.priority}
                          size="small"
                          color={getPriorityColor(rec.priority)}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {rec.rationale}
                      </Typography>
                    </Card>
                  ))}
                </Grid>
              )}

              {/* Metadata */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Analysis Date</Typography>
                    <Typography variant="body2">
                      {ai_insight.analysis_date 
                        ? new Date(ai_insight.analysis_date).toLocaleString() 
                        : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Stale Apps Card */}
      {stale_apps && stale_apps.length > 0 && (
        <Accordion 
          expanded={expanded === 'stale-apps'} 
          onChange={handleAccordionChange('stale-apps')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AppsIcon color="primary" />
              Stale Applications ({stale_apps.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {stale_apps.map((app, index) => (
                <Grid item xs={12} key={index}>
                  <Card sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Application</Typography>
                        <Typography variant="body1">{app.app_name || 'Unknown'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Last Accessed</Typography>
                        <Typography variant="body1">
                          {app.last_accessed 
                            ? new Date(app.last_accessed).toLocaleDateString() 
                            : 'Never'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Days Since Access</Typography>
                        <Typography variant="body1">{app.days_since_access ?? 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Access Count</Typography>
                        <Typography variant="body1">{app.access_count ?? 0}</Typography>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Metadata Footer */}
      <Card sx={{ p: 2, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Analysis performed with {metadata?.days_threshold || daysThreshold} days threshold
        </Typography>
        {metadata?.analysis_timestamp && (
          <Typography variant="caption" color="text.secondary" display="block">
            Last updated: {new Date(metadata.analysis_timestamp).toLocaleString()}
          </Typography>
        )}
      </Card>
    </Box>
  );
}
